"use client";

import { useMounted } from "@/hooks/use-mounted";
import { useEditorStore } from "@/store/theme-editor-store";
import { extractFontFamily, getDefaultWeights, isLocalFont } from "@/lib/theme/utils/fonts";
import { loadGoogleFont } from "@/lib/theme/utils/fonts/google-fonts";
import { useEffect, useMemo } from "react";

export function DynamicFontLoader() {
  const { themeState } = useEditorStore();
  const isMounted = useMounted();

  const fontSans = themeState.styles[themeState.currentMode]["font-sans"];
  const fontSerif = themeState.styles[themeState.currentMode]["font-serif"];
  const fontMono = themeState.styles[themeState.currentMode]["font-mono"];

  const currentFonts = useMemo(() => {
    return {
      sans: fontSans,
      serif: fontSerif,
      mono: fontMono,
    } as const;
  }, [fontSans, fontSerif, fontMono]);

  useEffect(() => {
    if (!isMounted) return;

    try {
      Object.entries(currentFonts).forEach(([_type, fontValue]) => {
        const fontFamily = extractFontFamily(fontValue);
        if (fontFamily) {
          // Skip Google Fonts loading for local fonts (they're already loaded via @font-face in globals.css)
          if (isLocalFont(fontFamily)) {
            return;
          }
          const weights = getDefaultWeights(["400", "500", "600", "700"]);
          loadGoogleFont(fontFamily, weights);
        }
      });
    } catch (e) {
      console.warn("DynamicFontLoader: Failed to load Google fonts:", e);
    }
  }, [isMounted, currentFonts]);

  return null;
}
